export default function initAutosuggest({
  wrapper,
  inputSelector,
  listSelector,
  endpoint,
  minChars = 2,
  debounceMs = 250,
  onSelect = null,
}) {
  const input = wrapper.querySelector(inputSelector);
  const list = wrapper.querySelector(listSelector);

  if (!input || !list) return;

  // Ensure a stable id for aria-activedescendant ids
  if (!wrapper.dataset.autosuggestId) {
    wrapper.dataset.autosuggestId = `as-${Math.random().toString(36).slice(2, 9)}`;
  }

  let debounce;
  let controller;
  let activeIndex = -1;
  let items = [];

  // Used to ignore late fetch responses and to suppress fetching right after selection
  let requestSeq = 0;
  let suppressNextInput = false;

  function clearList() {
    list.innerHTML = '';
    items = [];
    activeIndex = -1;
    input.removeAttribute('aria-activedescendant');
  }

  function closeList() {
    list.classList.add('hidden');
    input.setAttribute('aria-expanded', 'false');
    input.removeAttribute('aria-activedescendant');
    activeIndex = -1;
  }

  function openList() {
    list.classList.remove('hidden');
    input.setAttribute('aria-expanded', 'true');
  }

  function setActive(index) {
    items.forEach((el, i) => {
      el.classList.toggle('selected', i === index);
    });

    activeIndex = index;

    const activeItem = items[index];
    if (!activeItem) return;

    input.setAttribute('aria-activedescendant', activeItem.id);
    activeItem.scrollIntoView({ block: 'nearest' });
  }

  function applyValue(value) {
    // Prevent programmatic value set from triggering fetch/open
    suppressNextInput = true;

    input.value = value;

    // Hide + wipe immediately
    clearList();
    closeList();

    // Let normal user input work again on the next tick
    queueMicrotask(() => {
      suppressNextInput = false;
    });
  }

  function selectItem(index) {
    const item = items[index];
    if (!item) return;

    const value = item.textContent;

    // Abort any in-flight request so it can't re-open the list when it returns
    if (controller) controller.abort();

    // Invalidate any pending results (extra safety)
    requestSeq++;

    if (typeof onSelect === 'function') {
      onSelect(value, item.dataset);
    } else {
      applyValue(value);
    }

    // Always broadcast selection so other instances can sync + close
    document.dispatchEvent(
      new CustomEvent('autosuggest:selected', {
        detail: {
          value,
          sourceInput: input,
        },
      })
    );

    // Ensure THIS instance closes too (in case onSelect handled only syncing)
    clearList();
    closeList();
  }

  // Listen for other autosuggest instances selecting a value
  function onExternalSelect(e) {
    const { value, sourceInput } = e.detail || {};
    if (!value) return;

    // ignore our own event only
    if (sourceInput === input) return;

    applyValue(value);
  }

  document.addEventListener('autosuggest:selected', onExternalSelect);

  input.addEventListener('input', () => {
    if (suppressNextInput) return;

    const query = input.value.trim();
    clearTimeout(debounce);

    if (query.length < minChars) {
      clearList();
      closeList();
      return;
    }

    debounce = setTimeout(() => {
      if (controller) controller.abort();
      controller = new AbortController();

      const mySeq = ++requestSeq;

      fetch(`${endpoint}?q=${encodeURIComponent(query)}`, {
        signal: controller.signal,
        headers: { Accept: 'application/json' },
      })
        .then(res => (res.ok ? res.json() : Promise.reject(res)))
        .then(({ data }) => {
          // If something else happened since this request started, ignore it
          if (mySeq !== requestSeq) return;

          clearList();

          if (!data?.length) {
            closeList();
            return;
          }

          data.forEach((result, index) => {
            const li = document.createElement('li');
            li.id = `${wrapper.dataset.autosuggestId}-option-${index}`;
            li.setAttribute('role', 'option');
            li.className = 'suggestions-item';
            li.textContent = result.title;

            // Store extra payload
            Object.keys(result).forEach(key => {
              if (key !== 'title') {
                li.dataset[key] = result[key];
              }
            });

            li.addEventListener('mouseenter', () => setActive(index));
            li.addEventListener('click', () => selectItem(index));

            list.appendChild(li);
            items.push(li);
          });

          openList();
        })
        .catch(err => {
          if (err?.name !== 'AbortError') {
            clearList();
            closeList();
          }
        });
    }, debounceMs);
  });

  input.addEventListener('keydown', e => {
    if (!items.length) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setActive(activeIndex === -1 ? 0 : (activeIndex + 1) % items.length);
        break;

      case 'ArrowUp':
        e.preventDefault();
        setActive(
          activeIndex === -1 ? items.length - 1 : (activeIndex - 1 + items.length) % items.length
        );
        break;

      case 'Enter':
        if (activeIndex >= 0) {
          e.preventDefault();
          selectItem(activeIndex);
        } else {
          closeList(); // allow normal form submit
        }
        break;

      case 'Escape':
        clearList();
        closeList();
        break;
    }
  });

  document.addEventListener('click', e => {
    if (!wrapper.contains(e.target)) {
      clearList();
      closeList();
    }
  });
}
